import React, { useContext } from "react";
import { Button as AntdButton, Col, Row, ButtonProps as AntdButtonProps, message } from "antd";
import { RiFileEditLine } from "react-icons/ri";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import { useForm, FormProvider } from "react-hook-form";
import Controller from "../../../form-components/controller";
import Input from "../../../form-components/input";
import { IQuantity } from "../../../../models/product/response";
import { IMatchQuantity } from "../../../../models/product/request";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
}

interface IFormValues {
  stores: {
    storeId: number;
    storeName: string | { ar: string; en: string };
    enteredQuantity: string;
    withdrawnQuantity: string;
  }[];
}

// Helper function to get display name from store_name
const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === 'string') return name;
  return name?.ar || name?.en || '-';
};

const MatchQuantityButton: React.FC<ButtonProps> = ({ passedId, quantities, ...props }) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const methods = useForm<IFormValues>({
      defaultValues: {
        stores: quantities.map((q) => ({
          storeId: q.store_id,
          storeName: q.store_name,
          enteredQuantity: q.entered_quantity?.toString() ?? "0",
          withdrawnQuantity: q.withdrawn_quantity?.toString() ?? "0",
        })),
      },
    });

    const { control } = methods as any;

    const handleConfirm = async () => {
      const isValid = await methods.trigger();

      if (!isValid) {
        return;
      }

      const data = methods.getValues();

      // Check if at least one store has non-zero values
      const hasAtLeastOneChange = data.stores.some(
        (s) => Number(s.enteredQuantity) !== 0 || Number(s.withdrawnQuantity) !== 0
      );

      if (!hasAtLeastOneChange) {
        message.error(t('at_least_one_quantity_required'));
        return;
      }

      const payload: IMatchQuantity = {
        storeQuantities: data.stores.map((s) => ({
          store_id: s.storeId,
          entered_quantity: Number(s.enteredQuantity),
          withdrawn_quantity: Number(s.withdrawnQuantity),
        })),
      };

      await actions.matchQuantity(passedId, payload);
      actions.getData();
      hideFormModal();
    };

    return (
      <FormHookModal
        id="match-quantity-modal"
        confirmButtonText={t("match")}
        loading={loading.includes("matchQuantity")}
        title={t("match_quantity")}
        hideModal={hideFormModal}
        width="50vw"
        style={{ marginTop: "1.6rem", maxHeight: "70vh", overflowY: "auto" }}
        onSubmit={() => {}}
        confirmClick={handleConfirm}
      >
        <FormProvider {...methods}>
          {quantities.map((q, index) => (
            <Row key={q.store_id} gutter={[16, 8]} style={{ marginBottom: 8 }}>
              <Col span={12}>
                <strong>{getStoreName(q.store_name)}</strong>
                <FormItem label={t("entered_quantity")}>
                  <Controller
                    name={`stores.${index}.enteredQuantity`}
                    control={control}
                    rules={{
                      validate: (value) => {
                        const numValue = Number(value);
                        if (isNaN(numValue)) {
                          return t("must_be_a_number");
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem label={t("withdrawn_quantity")}>
                  <Controller
                    name={`stores.${index}.withdrawnQuantity`}
                    control={control}
                    rules={{
                      validate: (value) => {
                        const numValue = Number(value);
                        if (isNaN(numValue)) {
                          return t("must_be_a_number");
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  />
                </FormItem>
              </Col>
            </Row>
          ))}
        </FormProvider>
      </FormHookModal>
    );
  }, [passedId, quantities, loading]);

  return (
    <AntdButton type="primary" icon={<RiFileEditLine />} onClick={showFormModal} {...props} />
  );
};

export default MatchQuantityButton;
